import { Component, OnDestroy, OnInit } from '@angular/core';
import { appConstants } from '../../../shared/constants/app-constants';
import { IUser } from '../../../shared/models/user';
import { AuthService } from '../../../shared/services/auth.service';
import { catchError, combineLatest, filter, map, Observable, of, Subscription, switchMap, take, tap } from 'rxjs';
import { UserUtility } from '../../../shared/utilities/user-utility';
import { StatusBadgeColourScheme } from '../../controls/status-badge/status-badge-constants';
import { ProjectService } from '../shared/services/project.service';
import { IProjectUpdates } from '../shared/models/project-change';
import { IProjectPhase } from '../shared/models/project-phase';
import { IApiResponse } from '../../../shared/models/api-response';
import { ToastService } from '../../../shared/services/toast.service';


interface IProjectUpdate {
  projectId: number;
  title: string;
  description: string;
  date: Date;
  phase: string;
}

@Component({
    selector: 'app-project-updates',
    templateUrl: './project-updates.component.html',
    styleUrl: './project-updates.component.scss',
    standalone: false
})
export class ProjectUpdatesComponent implements OnInit, OnDestroy {

  public statusBadgeColourSchemeEnum = StatusBadgeColourScheme;

  loading: boolean = true;
  subscriptions: Subscription = new Subscription();

  currentUser!: IUser;

  projects: IProjectUpdate[] = [];
  expectedPhases: string[] = [];

  viewProjectsButtonLabel: string = 'View My Projects';
  myProjects: string = appConstants.projects.viewTypes.myProjects;

  constructor(
    private _authService: AuthService,
    private _projectService: ProjectService,
    private _toastService: ToastService
  ) { }

  ngOnInit(): void {
    const userSubscription = this._authService.currentUserSubject
    .pipe(
      filter(user => !!user),
      take(1),
      tap((user) => {
        this.currentUser = user as IUser;
        this.loading = true;
      }),
      map((user) => {
        const committeeCodes = UserUtility.getCommitteeCodes(user as IUser);
        return committeeCodes;
      }),
      switchMap((committeeCodes: string[]) => {
        return combineLatest([
          this.getProjectUpdates(committeeCodes),
          this.getProjectPhases()
        ])
      })
    )
    .subscribe({
      next: ([projects, phases]) => {
        this.projects = projects;
        this.expectedPhases = phases;
      },
      error: (error) => {
        console.log(error);
      },
      complete: () => {
        this.loading = false;
      }
    });

    this.subscriptions.add(userSubscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  getProjectUpdates(committeeCodes: string[]) : Observable<IProjectUpdate[]> {

    const obs$ = this._projectService.getProjectsUpdates(committeeCodes)
    .pipe(
      map((response : IApiResponse<IProjectUpdates>) => {
          if(response?.success) {

            let projectChanges = response.content?.projectChanges;

            let projects = projectChanges?.map(project => {
              return {
                projectId: project?.projectId,
                title: project?.designation,
                description: `This project has moved from ${project?.previousPhase} to ${project?.phase}`,
                date: project?.lastUpdated,
                phase: project?.phase
              }

            })??[];

            projects = projects.sort((a, b) => new Date(b.date)?.getTime() - new Date(a.date)?.getTime());

            return projects;

          } else {
            this._toastService.showError("Unable to get the latest project updates.", "Projects Update");
            return [];
          }
      }),
      catchError((error) => {
        this._toastService.showError("Unable to get the latest project updates.", "Projects Update");
        return of([]);
      })
    );

    return obs$;
  }

  getProjectPhases() : Observable<string[]> {
    const obs$ = this._projectService.getPhases()
    .pipe(
      map((response: IApiResponse<IProjectPhase[]>) => {
        if(response?.success) {
          const phases = response.content.map(x => x.description);
          return phases;
        } else {
          this._toastService.showError("Unable to get the project phases.", "Projects Update");
          return [];
        }
      }),
      catchError((error) => {
        this._toastService.showError("Unable to get the latest project phases.", "Projects Update");
        return of([]);
      })
    )
    return obs$;
  }

  getProjectsByPhase(phase: string): number {
    return this.projects?.filter(p => p.phase === phase).length;
  }
}

